import React, { useState } from "react";
import PropTypes from "prop-types";
import { injectIntl, intlShape } from "react-intl";
import Drawer from "@material-ui/core/Drawer";
import { useDispatch } from "react-redux";
import { Checkbox } from "@material-ui/core";
import generateAction from "../../../utils/action-helpers";
import { DETAIL } from "../../../state/actions";
import Table from "../../ui/table";
import AddEditForm from "./add-edit-form/index";
import {
  toFormattedNumber,
  toHibridSocietyFormat,
  toHibridDivisionFormat,
  toHibridClaimAreaFormat,
  toHibridAccountingkeyFormat
} from "../../../utils/format";
import { accountingKeySign, majorAccountSign } from "../../../utils/sap-mapper";
import removeIcon from "../../../assets/img/shredder.svg";
import TooltipButton from "../../ui/tooltip-button";
import Disclaimer from "../../ui/disclaimer";
import { calculateCurency } from ".";

const ManualManagement = ({ intl, withCheck, toggleSelect, data, canEdit, currency }) => {
  const currencyText = currency ? calculateCurency(currency) : "€";
  const { messages } = intl;
  const [openedModal, setOpenedModal] = useState(false);
  const [indexToRemove, setIndexToRemove] = useState(-1);
  const [openedDrawer, setOpenedDrawer] = useState(-1);
  const dispatch = useDispatch();
  const accountingKeyColumn =
    data && data[0] && data[0].accountingKey
      ? {
          name: "accountingKey",
          label: data[0].accountingKey ? messages.accountingKey : messages.ceco,
          options: {
            cellRenderer: value => {
              const { ceco, accountingKey } = value.row.original;
              return accountingKey ? toHibridAccountingkeyFormat(accountingKey) : `CECO: ${ceco}`;
            }
          }
        }
      : {
          name: "ceco",
          label: messages.ceco,
          options: {
            cellRenderer: value => {
              const { ceco } = value.row.original;
              return ceco || "";
            }
          }
        };
  const columns = [
    {
      name: "selected",
      label: " ",
      options: {
        filter: false,
        cellRenderer: value => {
          const { original, index } = value.row;
          return (
            <Checkbox
              edge="start"
              className="input-check"
              checked={value.row.original.selected}
              onChange={() => toggleSelect(original, index)}
            />
          );
        }
      }
    },
    {
      name: "customer",
      label: messages.sapCode
    },
    accountingKeyColumn,
    {
      name: "society",
      label: messages.society,
      options: {
        cellRenderer: value => toHibridSocietyFormat(value.row.original.society)
      }
    },
    {
      name: "division",
      label: messages.division,
      options: {
        cellRenderer: value => toHibridDivisionFormat(value.row.original.division)
      }
    },
    {
      name: "recArea",
      label: data && data[0] && data[0].recArea ? messages.recArea : "",
      options: {
        cellRenderer: value => {
          const { society, recArea } = value.row.original;
          return recArea && society ? toHibridClaimAreaFormat(society, recArea) : "";
        }
      }
    },
    {
      name: "amount",
      label: messages.amount,
      options: {
        cellRenderer: value => {
          const { accountingKey, customer } = value.row.original;
          const sign = accountingKey
            ? accountingKeySign(accountingKey)
            : majorAccountSign(customer);
          return (
            <div className="text-right mr-2">
              {`${toFormattedNumber(value.row.original.amount * sign)}${currencyText}`}
            </div>
          );
        }
      }
    },
    {
      label: messages.empty,
      options: {
        sort: false,
        filter: false,
        cellRenderer: value => {
          const { index, original } = value.row;
          return (
            <>
              <div className="manual-management-toolbar">
                {canEdit && (
                  <TooltipButton
                    buttonClassName="btn-action"
                    onClick={() => {
                      setOpenedModal(true);
                      setIndexToRemove(index);
                    }}
                    iconClassName="icono-clear"
                    tooltipText="Eliminar fila"
                  />
                )}
                {!withCheck && (
                  <TooltipButton
                    buttonClassName="btn-action"
                    onClick={() => setOpenedDrawer(index)}
                    iconClassName="icono-write"
                    tooltipText="Editar la información de la fila"
                  />
                )}
              </div>
              <Drawer
                className="sidebar-container"
                open={openedDrawer === index}
                variant="persistent"
                anchor="right"
              >
                <AddEditForm
                  currency={currency}
                  close={() => setOpenedDrawer(-1)}
                  isEditing
                  index={index}
                  editingObject={original}
                  canEdit={canEdit}
                />
                <Disclaimer
                  icon={<img src={removeIcon} alt="Eliminar partida" />}
                  title="Eliminar partida manual"
                  text="Perdeá la información que ha introducido al crear la partida. Esta acción no se puede deshacer."
                  visible={openedModal}
                  acepptButtonText="Eliminar"
                  cancelButtonText="Cancelar"
                  onCancel={() => setOpenedModal(false)}
                  onAccept={() => {
                    setOpenedModal(false);
                    dispatch(generateAction(DETAIL.REMOVE_MANUAL, indexToRemove));
                  }}
                />
              </Drawer>
              {openedDrawer === index && <div className="sidebar-back fade show" />}
            </>
          );
        }
      }
    }
  ];

  const tableOptions = {
    pagination: false,
    filter: false,
    rowExpansion: false,
    hasExpansion: false,
    toolbar: false,
    emptyElement: <span>No hay elementos para el cliente</span>
  };

  return (
    <Table
      className="manual-management"
      columns={columns.filter(x => x.name !== "selected" || withCheck)}
      data={data || []}
      options={tableOptions}
    />
  );
};

ManualManagement.propTypes = {
  intl: intlShape.isRequired,
  data: PropTypes.arrayOf(),
  canEdit: PropTypes.bool,
  currency: PropTypes.string,
  withCheck: PropTypes.bool,
  toggleSelect: PropTypes.func
};

ManualManagement.defaultProps = {
  data: [],
  canEdit: false,
  currency: "EUR",
  withCheck: false,
  toggleSelect: () => {}
};

export default injectIntl(ManualManagement);
